/**
 * Image management — operator tool for inspecting, retagging, deleting,
 * and reclassifying images held by cynapse.
 *
 * Top-level page at /images. Built to diagnose cases like the Santos
 * publish-with-zero-images incident: load by ?announcement_id= to see
 * exactly what cynapse stored for that announcement, including the
 * Gemini rationale, and decide whether to retag, delete, or rerun
 * classification with richer context.
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Image as ImageIcon,
  Search,
  Filter,
  X,
  AlertTriangle,
  Loader2,
  Download,
} from 'lucide-react';
import {
  listImages,
  sourceEntityImages,
} from '../lib/api/images.js';
// The grid tile and the detail drawer are shared with the entity workbench's
// Images tab — one rendering of the role/eligibility model, not two.
import { ImageCard, toLightboxItem } from '../components/images/ImageCard.js';
import { ImageDetailDrawer } from '../components/images/ImageDetailDrawer.js';
import { ImageLightbox } from '../components/images/ImageLightbox.js';
import { listEntities } from '../lib/api/entities.js';
import { listAnnouncements, getAnnouncement } from '../lib/api/announcements.js';
import { getEntity } from '../lib/api/entities.js';
import type {
  ImageListParams,
  ImageOwnership,
  ImageRole,
  ImageType,
} from '../lib/api/types.js';

const IMAGE_TYPES: ImageType[] = [
  'logo', 'operational', 'personnel', 'facility',
  'chart', 'graph', 'map', 'diagram', 'cross_section',
  'photo', 'illustration', 'generated', 'other', 'uncertain',
];
const IMAGE_ROLES: ImageRole[] = ['data_figure', 'editorial_photo', 'decorative', 'logo'];
const OWNERSHIPS: ImageOwnership[] = ['proprietary', 'public_domain', 'generic', 'unknown'];

const PAGE_SIZE = 50;

function parseList(raw: string | null): string[] | undefined {
  if (!raw) return undefined;
  const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
  return parts.length ? parts : undefined;
}

function parseBool(raw: string | null): boolean | undefined {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return undefined;
}

function parseNumber(raw: string | null): number | undefined {
  if (raw === null || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function ImagesPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);

  const filters: ImageListParams = useMemo(() => ({
    announcement_id: searchParams.get('announcement_id') ?? undefined,
    entity_id: searchParams.get('entity_id') ?? undefined,
    image_type: parseList(searchParams.get('image_type')) as ImageType[] | undefined,
    image_role: parseList(searchParams.get('image_role')) as ImageRole[] | undefined,
    ownership_type: parseList(searchParams.get('ownership_type')) as ImageOwnership[] | undefined,
    min_quality: parseNumber(searchParams.get('min_quality')),
    low_confidence: parseBool(searchParams.get('low_confidence')),
    has_description: parseBool(searchParams.get('has_description')),
    search: searchParams.get('search') ?? undefined,
    limit: PAGE_SIZE,
    offset: parseNumber(searchParams.get('offset')) ?? 0,
  }), [searchParams]);

  const listQuery = useQuery({
    queryKey: ['images', filters],
    queryFn: () => listImages(filters),
    placeholderData: keepPreviousData,
  });

  const updateFilter = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete('offset');
    setSearchParams(next, { replace: true });
  };

  const toggleListFilter = (key: string, value: string) => {
    const current = parseList(searchParams.get(key)) ?? [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateFilter(key, next.length ? next.join(',') : undefined);
  };

  const setOffset = (offset: number) => {
    const next = new URLSearchParams(searchParams);
    if (offset <= 0) next.delete('offset');
    else next.set('offset', String(offset));
    setSearchParams(next, { replace: true });
  };

  const total = listQuery.data?.total ?? 0;
  const offset = filters.offset ?? 0;
  const images = listQuery.data?.images ?? [];

  const sourceEntityMutation = useMutation({
    mutationFn: (entityId: string) => sourceEntityImages(entityId),
    onSuccess: () => {
      // Don't invalidate the list immediately — the job is async and
      // images won't appear for a minute or two. The status message
      // from the response tells the operator to come back.
    },
  });

  return (
    <div className="max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <ImageIcon className="h-7 w-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Images</h1>
            <p className="text-sm text-gray-600">
              Inspect, retag, delete, and reclassify images held by Cynapse.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {filters.entity_id && (
            <button
              onClick={() => {
                if (!filters.entity_id) return;
                if (!confirm(
                  'Trigger MarketIndex backlog sourcing for this entity?\n\n'
                  + 'Runs in the background — re-extracts images from already-'
                  + 'ingested announcements (Track 1) and pulls image-rich '
                  + 'reports (annual / quarterly / AGM / sustainability / ESG / '
                  + 'technical) from MarketIndex (Track 2). New images appear '
                  + 'in a minute or two; refresh /images then to see them.'
                )) return;
                sourceEntityMutation.mutate(filters.entity_id);
              }}
              disabled={sourceEntityMutation.isPending}
              className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm flex items-center gap-2 disabled:opacity-50"
              title="Run Track 1 (re-extract) + Track 2 (MarketIndex backlog) for the selected entity"
            >
              {sourceEntityMutation.isPending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Download className="h-4 w-4" />}
              Source from MarketIndex
            </button>
          )}
          <div className="text-sm text-gray-500">
            {listQuery.isLoading
              ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading…</span>
              : `${total} image${total === 1 ? '' : 's'}`}
          </div>
        </div>
      </header>

      {sourceEntityMutation.isSuccess && sourceEntityMutation.data && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-800">
          {sourceEntityMutation.data.message}
          {sourceEntityMutation.data.job_id && (
            <span className="ml-2 text-emerald-600 font-mono text-xs">
              job {sourceEntityMutation.data.job_id.slice(0, 8)}
            </span>
          )}
        </div>
      )}
      {sourceEntityMutation.isError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Source trigger failed: {(sourceEntityMutation.error as Error).message}
        </div>
      )}

      <FilterBar
        filters={filters}
        rawParams={searchParams}
        onTextFilter={updateFilter}
        onToggleList={toggleListFilter}
      />

      {listQuery.error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Failed to load images: {(listQuery.error as Error).message}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {images.map((img, i) => (
          <ImageCard
            key={img.image_id}
            image={img}
            onClick={() => setSelectedId(img.image_id)}
            onZoom={() => setZoomIndex(i)}
          />
        ))}
        {!listQuery.isLoading && images.length === 0 && (
          <div className="col-span-full text-center text-gray-500 py-10">
            No images match the current filters.
          </div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="mt-6 flex items-center justify-between text-sm">
          <span className="text-gray-600">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Prev
            </button>
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {zoomIndex !== null && images[zoomIndex] && (
        <ImageLightbox
          items={images.map(toLightboxItem)}
          index={zoomIndex}
          onIndex={setZoomIndex}
          onClose={() => setZoomIndex(null)}
        />
      )}

      {selectedId && (
        <ImageDetailDrawer
          imageId={selectedId}
          onClose={() => setSelectedId(null)}
          onChange={() => {
            queryClient.invalidateQueries({ queryKey: ['images'] });
            queryClient.invalidateQueries({ queryKey: ['image-detail', selectedId] });
            queryClient.invalidateQueries({ queryKey: ['image-history', selectedId] });
          }}
          onDeleted={() => {
            setSelectedId(null);
            queryClient.invalidateQueries({ queryKey: ['images'] });
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Filter bar
// ============================================================================

function FilterBar(props: {
  filters: ImageListParams;
  rawParams: URLSearchParams;
  onTextFilter: (key: string, value: string | undefined) => void;
  onToggleList: (key: string, value: string) => void;
}) {
  const { filters, rawParams, onTextFilter, onToggleList } = props;
  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  useEffect(() => setSearchInput(filters.search ?? ''), [filters.search]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onTextFilter('search', searchInput || undefined);
  };

  const activeTypes = parseList(rawParams.get('image_type')) ?? [];
  const activeRoles = parseList(rawParams.get('image_role')) ?? [];
  const activeOwnerships = parseList(rawParams.get('ownership_type')) ?? [];
  const lowConf = filters.low_confidence;

  return (
    <form onSubmit={submitSearch} className="mb-6 space-y-3 p-4 bg-surface border border-gray-200 rounded-lg">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="block">
          <span className="text-xs font-medium text-gray-700">Announcement</span>
          <RemotePicker
            value={filters.announcement_id ?? null}
            onChange={(id) => onTextFilter('announcement_id', id ?? undefined)}
            resolveLabel={resolveAnnouncementLabel}
            fetcher={searchAnnouncements}
            placeholder="Search title or company…"
          />
        </div>
        <div className="block">
          <span className="text-xs font-medium text-gray-700">Entity</span>
          <RemotePicker
            value={filters.entity_id ?? null}
            onChange={(id) => onTextFilter('entity_id', id ?? undefined)}
            resolveLabel={resolveEntityLabel}
            fetcher={searchEntities}
            placeholder="Search by name (e.g. Santos)"
          />
        </div>
        <label className="block md:col-span-2">
          <span className="text-xs font-medium text-gray-700">Search caption / alt / AI description</span>
          <div className="mt-1 flex gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="e.g. waitsia, drill rig, CEO name"
              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <button
              type="submit"
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm flex items-center gap-1"
            >
              <Search className="h-4 w-4" />
              Apply
            </button>
          </div>
        </label>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
        <FilterChipGroup
          label="image_type"
          options={IMAGE_TYPES}
          active={activeTypes}
          onToggle={(v) => onToggleList('image_type', v)}
        />
        <FilterChipGroup
          label="image_role"
          options={IMAGE_ROLES}
          active={activeRoles}
          onToggle={(v) => onToggleList('image_role', v)}
        />
        <FilterChipGroup
          label="ownership_type"
          options={OWNERSHIPS}
          active={activeOwnerships}
          onToggle={(v) => onToggleList('ownership_type', v)}
        />
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700">low_confidence</span>
          <button
            type="button"
            onClick={() => onTextFilter('low_confidence', lowConf ? undefined : 'true')}
            className={`px-2 py-0.5 rounded border ${lowConf ? 'bg-yellow-100 border-yellow-400 text-yellow-900' : 'border-gray-300 text-gray-600'}`}
          >
            only
          </button>
        </div>
      </div>
    </form>
  );
}

// ============================================================================
// Remote picker — debounced typeahead combobox with id-as-value and label
// resolution from the selected id when the page loads with that filter
// already populated (deep link / browser back).
// ============================================================================

interface PickerOption {
  id: string;
  label: string;
  sublabel?: string;
}

async function searchEntities(query: string): Promise<PickerOption[]> {
  const res = await listEntities({ search: query, page_size: 10 });
  return res.items.map((e) => ({
    id: e.entity_id,
    label: e.display_name,
    sublabel: e.canonical_identifier,
  }));
}

async function searchAnnouncements(query: string): Promise<PickerOption[]> {
  const res = await listAnnouncements({ search: query, limit: 10 });
  return res.items.map((a) => ({
    id: a.announcement_id,
    label: a.announcement_title,
    sublabel: `${a.company_code} · ${a.announcement_date?.slice(0, 10) ?? ''}`,
  }));
}

async function resolveEntityLabel(id: string): Promise<string> {
  try {
    const e = await getEntity(id);
    return e.display_name || id.slice(0, 8);
  } catch {
    return id.slice(0, 8);
  }
}

async function resolveAnnouncementLabel(id: string): Promise<string> {
  try {
    const a = await getAnnouncement(id);
    return a.announcement_title || id.slice(0, 8);
  } catch {
    return id.slice(0, 8);
  }
}

function RemotePicker(props: {
  value: string | null;
  onChange: (id: string | null) => void;
  fetcher: (q: string) => Promise<PickerOption[]>;
  resolveLabel: (id: string) => Promise<string>;
  placeholder?: string;
  minChars?: number;
}) {
  const minChars = props.minChars ?? 2;
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [resolvedLabel, setResolvedLabel] = useState<string | null>(null);

  // Debounce query input.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Resolve the label for a deep-linked / browser-back value.
  useEffect(() => {
    let cancelled = false;
    if (!props.value) {
      setResolvedLabel(null);
      return;
    }
    setResolvedLabel(null);
    props.resolveLabel(props.value).then((label) => {
      if (!cancelled) setResolvedLabel(label);
    });
    return () => { cancelled = true; };
  }, [props.value, props.resolveLabel]);

  const optionsQuery = useQuery({
    queryKey: ['picker-options', props.placeholder, debounced],
    queryFn: () => props.fetcher(debounced),
    enabled: open && debounced.length >= minChars,
    placeholderData: keepPreviousData,
  });

  if (props.value) {
    return (
      <div className="mt-1 flex items-center gap-2 px-2 py-1 border border-gray-300 rounded bg-blue-50 text-sm">
        <span className="truncate text-blue-900" title={props.value}>
          {resolvedLabel ?? <span className="text-gray-400">resolving…</span>}
        </span>
        <button
          type="button"
          onClick={() => { props.onChange(null); setQuery(''); }}
          className="ml-auto p-0.5 rounded hover:bg-blue-100"
          aria-label="Clear"
        >
          <X className="h-3.5 w-3.5 text-blue-700" />
        </button>
      </div>
    );
  }

  const options = optionsQuery.data ?? [];

  return (
    <div className="mt-1 relative">
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={props.placeholder}
        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
      />
      {open && debounced.length >= minChars && (
        <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-surface border border-gray-200 rounded shadow-lg max-h-64 overflow-y-auto">
          {optionsQuery.isFetching && (
            <div className="px-2 py-1 text-xs text-gray-500 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> searching…
            </div>
          )}
          {!optionsQuery.isFetching && options.length === 0 && (
            <div className="px-2 py-1 text-xs text-gray-500">No matches.</div>
          )}
          {options.map((opt) => (
            <button
              type="button"
              key={opt.id}
              onMouseDown={(e) => {
                // mousedown beats the input's onBlur — onClick would
                // never fire because the dropdown gets torn down first.
                e.preventDefault();
                props.onChange(opt.id);
                setQuery('');
                setOpen(false);
              }}
              className="w-full text-left px-2 py-1.5 hover:bg-blue-50 text-sm border-b last:border-b-0 border-gray-100"
            >
              <div className="truncate font-medium text-gray-900">{opt.label}</div>
              {opt.sublabel && (
                <div className="truncate text-xs text-gray-500">{opt.sublabel}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChipGroup(props: {
  label: string;
  options: readonly string[];
  active: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className="font-medium text-gray-700 mr-1">{props.label}:</span>
      {props.options.map((opt) => {
        const isActive = props.active.includes(opt);
        return (
          <button
            type="button"
            key={opt}
            onClick={() => props.onToggle(opt)}
            className={`px-2 py-0.5 rounded border ${isActive ? 'bg-blue-100 border-blue-400 text-blue-900' : 'border-gray-300 text-gray-600 hover:border-gray-500'}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}
